import { createSignal, type JSX, Show } from 'solid-js';
import { ArtifactFilterBar } from '../components/ArtifactFilterBar';
import { ArtifactLinkPanel } from '../components/ArtifactLinkPanel';
import { ArtifactTable } from '../components/ArtifactTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { Skeleton } from '../components/Skeleton';
import { Toast } from '../components/Toast';
import { t } from '../i18n';
import { createArtifactActions } from '../lib/artifact-actions';
import { ARTIFACT_PAGE_MAX, type ArtifactFilter, useArtifacts } from '../lib/artifacts';
import { SESSION_TOKEN } from '../lib/session';

// Artifacts browser (arc 101 slice 08) — the human door onto `/v1/artifacts` and
// the MCP `artifact` verb. CONTAINER: it owns the read, the applied filter and the
// re-reads; <ArtifactTable/> paints rows and the actions controller holds state.
//
// THE LIST IS A TRUNCATABLE HEAD, and the screen says so. `GET /v1/artifacts`
// serves `per_page` rows newest-first and mints NO cursor, so there is no
// Load-more to offer — a button promising pages the server has no token for is a
// lie about the data. This asks for the ceiling and, when the answer comes back
// exactly full, the panel footer names the one real remedy: narrow by run, task or
// repo, which is what the filter bar is for.
//
// NO CLIENT ROLE GATE. `read:artifacts` is every role's and `write:artifacts` is
// member and up, so a refused share or delete renders the API's own 403 in the
// toast rather than a control that silently was not there — the same call
// `/account/templates` makes about `write:runners`.

export function Artifacts(props: { token?: string | null } = {}): JSX.Element {
  // `=== undefined`, not `??`: an injected `null` MEANS "no session".
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const [filter, setFilter] = createSignal<ArtifactFilter>({});
  const query = useArtifacts(filter, token);
  const actions = createArtifactActions({ token, onWritten: () => void query.refetch() });

  const confirming = () => actions.pending();
  const isDelete = (): boolean => confirming()?.kind === 'delete';
  const confirmId = (): string => confirming()?.artifact.id ?? '';

  return (
    <section class="page artifacts">
      <PageHeader title={t('artifacts.title')} desc={t('artifacts.subtitle')} />

      <Show when={token !== null} fallback={<EmptyState message={t('artifacts.no_session')} />}>
        <ArtifactFilterBar value={filter()} onApply={setFilter} />

        <Show when={actions.link()}>
          {(link) => <ArtifactLinkPanel link={link()} onDismiss={actions.dismissLink} />}
        </Show>

        {/* QueryBoundary, not a bare Show: loading must not read as "empty". */}
        <QueryBoundary
          query={query}
          isEmpty={(rows) => rows.length === 0}
          loading={<Skeleton height="14rem" />}
          empty={<EmptyState message={t('artifacts.empty')} />}
        >
          {(rows) => (
            <Panel
              title={t('artifacts.list_title')}
              {...(rows.length === ARTIFACT_PAGE_MAX
                ? { footer: t('artifacts.truncated', { max: String(ARTIFACT_PAGE_MAX) }) }
                : {})}
            >
              <ArtifactTable
                rows={rows}
                isBusy={actions.isBusy}
                onDownload={actions.download}
                onShare={actions.askShare}
                onDelete={actions.askDelete}
              />
            </Panel>
          )}
        </QueryBoundary>
      </Show>

      <ConfirmDialog
        open={confirming() !== null}
        title={t(isDelete() ? 'artifacts.delete_title' : 'artifacts.share_title', {
          id: confirmId(),
        })}
        desc={t(isDelete() ? 'artifacts.delete_desc' : 'artifacts.share_desc')}
        cancelLabel={t('artifacts.cancel')}
        actionLabel={t(isDelete() ? 'artifacts.delete' : 'artifacts.share')}
        destructive={isDelete()}
        onCancel={actions.cancel}
        onConfirm={actions.confirm}
      />
      <Toast message={actions.error()} tone="error" onDismiss={actions.clearError} />
    </section>
  );
}
